/**
 * CEX Trading state
 * Handles balances
 */
import { Address } from 'wagmi';
import {
  GET_CEX_HISTORY_ORDERS,
  GET_CEX_HISTORY_ORDERS_DONE,
  GET_CEX_OPEN_ORDERS,
  GET_CEX_OPEN_ORDERS_DONE,
  GET_CEX_ORDERS_FAIL,
  CANCEL_CEX_ORDER,
  CANCEL_CEX_ORDER_DONE,
  CANCEL_CEX_ORDER_FAIL,
  UPDATE_CEX_ORDER,
} from '../actions/cex_orders';

export enum ECEXOrdersTab {
  none,
  open,
  history,
}

export interface ICexFormattedOrder {
  uuid: string;
  wallet: Address;
  exchange: number;
  pair: string;
  type: number;
  side: number;
  base_amount: number;
  status: number;
  fillBaseAmount: number;
  created_at: number;
  updated_at: number;
  closed_at: number;
}

export interface ICEXOrdersState {
  loading: boolean;
  tab: ECEXOrdersTab;
  openOrders: ICexFormattedOrder[];
  historyOrders: ICexFormattedOrder[];
}

export enum ECEXOrderStatus {
  UNDEFINED = 'UNDEFINED',
  OPEN = 'OPEN',
  FILLED = 'FILLED',
  PARTIAL_FILLED = 'PARTIAL_FILLED',
  CANCEL = 'CANCEL',
  PARTIAL_CANCEL = 'PARTIAL_CANCEL',
  FAILED = 'FAILED',
}

interface ICEXOrderUpdateCreate {
  exchange: string;
  pair: string;
  uuid: string;
}

interface ICEXOrderUpdateState {
  closedAt: string;
  createdAt: string;
  status: ECEXOrderStatus;
  updatedAt: string;
  fillBaseAmount: number;
  fillQuoteAmount: number;
}

export interface ICEXOrderUpdate {
  create: ICEXOrderUpdateCreate;
  state: ICEXOrderUpdateState;
}

/**
 * By default all cexes are disabled
 */
const defaultState: ICEXOrdersState = {
  loading: false,
  tab: ECEXOrdersTab.none,
  openOrders: [],
  historyOrders: [],
};

const cexOrders = (
  state: ICEXOrdersState = defaultState,
  action: any
): ICEXOrdersState => {
  switch (action.type) {
  case GET_CEX_HISTORY_ORDERS:
  case GET_CEX_OPEN_ORDERS:
  case CANCEL_CEX_ORDER:
    return {
      ...state,
      loading: true,
    };
  case GET_CEX_HISTORY_ORDERS_DONE:
    return {
      ...state,
      tab: ECEXOrdersTab.history,
      historyOrders: action.payload,
      loading: false,
    };
  case GET_CEX_OPEN_ORDERS_DONE:
    return {
      ...state,
      tab: ECEXOrdersTab.open,
      openOrders: action.payload,
      loading: false,
    };
  case CANCEL_CEX_ORDER_DONE:
    return {
      ...state,
      loading: false,
      openOrders: state.openOrders.filter(
        (order) => order.uuid !== action.payload
      ),
      historyOrders: state.historyOrders.filter(
        (order) => order.uuid !== action.payload
      ),
    };
  case GET_CEX_ORDERS_FAIL:
    return defaultState;
  case CANCEL_CEX_ORDER_FAIL:
    return {
      ...state,
      loading: false,
    };
  case UPDATE_CEX_ORDER:
    return updateStateFromLiveOrder(state, action.payload);
  default:
    return state;
  }
};

const updateStateFromLiveOrder = (
  state: ICEXOrdersState,
  payload: ICEXOrderUpdate
): ICEXOrdersState => {
  // Step 1: If order is filled or canceled - remove it from open
  if (
    [ECEXOrderStatus.FILLED, ECEXOrderStatus.CANCEL].includes(
      payload.state.status
    )
  ) {
    const order = state.openOrders.find(
      (order) => order.uuid === payload.create.uuid
    );
    // Step 2: Find order to update
    if (order) {
      const openOrders = state.openOrders.filter(
        (order) => order.uuid !== payload.create.uuid
      );
      return {
        ...state,
        openOrders,
        historyOrders: [...state.historyOrders, order],
      };
    }
    return state; // can't update if we don't know what to update
  } else {
    // Update it filled otherwise
    return {
      ...state,
      openOrders: state.openOrders.map((order: ICexFormattedOrder) => {
        if (order.uuid === payload.create.uuid) {
          return {
            ...order,
            fillBaseAmount: payload.state.fillBaseAmount,
            fillQuoteAmount: payload.state.fillQuoteAmount,
          };
        }
        return order;
      }),
    };
  }
};

export default cexOrders;
